import { useState } from 'react';

import { DateFormElementAnswerSchema, DateFormElement as DateFormElementType, FormElementKind } from '@gearbelt/global';
import { TooltipArrow, TooltipPortal } from '@radix-ui/react-tooltip';
import { useFormContext } from 'react-hook-form';
import { z } from 'zod';

import { InformationIcon } from '@/components/icons/information';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { NativeDatePicker } from '@/components/ui/native-date-picker';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

import { DateConfigLabels } from '../config-field-labels';
import { ConfigLabel } from '../config-label';
import { FormFieldError } from '../form-field-error';

export const DateFormElement = ({
  element,
  value,
  onChange,
  onBlur,
  readonly,
  lockedNotLatest,
  error,
}: {
  element: DateFormElementType;
  value?: z.infer<typeof DateFormElementAnswerSchema>['value'];
  onChange: (value: z.infer<typeof DateFormElementAnswerSchema>['value']) => void;
  onBlur?: () => void;
  readonly?: boolean;
  lockedNotLatest?: boolean;
  error?: string;
}) => {
  // Convert ISO string to Date object
  const dateValue = value ? new Date(value) : undefined;

  const handleDateChange = (newDate: Date | null) => {
    if (!newDate) {
      onChange(null);
      return;
    }

    // Convert to ISO string for form submission
    onChange(newDate.toISOString());
  };

  // Determine which type of picker to show based on configuration
  const getDatePickerType = () => {
    if ((element.config?.showDate ?? true) && element.config?.showTime) {
      return 'datetime-local';
    } else if (!(element.config?.showDate ?? true) && element.config?.showTime) {
      return 'time';
    } else {
      return 'date';
    }
  };

  return (
    <div className="space-y-2">
      <div className="space-y-1">
        {(element.label || lockedNotLatest) && (
          <div className="flex items-center gap-1">
            <Label required={element.required}>{element.label}</Label>
            {lockedNotLatest && (
              <TooltipProvider>
                <Tooltip delayDuration={0}>
                  <TooltipTrigger asChild>
                    <button type="button" aria-label="Why can't I edit this completion date?" className="text-secondary-text inline-flex">
                      <InformationIcon size={16} />
                    </button>
                  </TooltipTrigger>
                  <TooltipPortal>
                    <TooltipContent side="top" className="bg-default-text max-w-[240px] text-xs text-white">
                      The completion date can only be edited on the latest schedule check record.
                      <TooltipArrow className="fill-default-text" />
                    </TooltipContent>
                  </TooltipPortal>
                </Tooltip>
              </TooltipProvider>
            )}
          </div>
        )}

        <NativeDatePicker
          className="cursor-pointer"
          type={getDatePickerType()}
          value={dateValue || null}
          onChange={handleDateChange}
          onBlur={onBlur}
          placeholder={`Select ${element.label}`}
          disabled={readonly}
          error={!!error}
        />
      </div>

      <FormFieldError error={error} />
    </div>
  );
};

export function DateFormElementMock({ element }: { element: DateFormElementType }) {
  const [dateValue, setDateValue] = useState<Date | null>(null);

  // Determine which type of picker to show based on configuration
  const getDatePickerType = () => {
    if ((element.config?.showDate ?? true) && element.config?.showTime) {
      return 'datetime-local';
    } else if (!(element.config?.showDate ?? true) && element.config?.showTime) {
      return 'time';
    } else {
      return 'date';
    }
  };

  return <NativeDatePicker type={getDatePickerType()} value={dateValue} onChange={setDateValue} placeholder={`Select ${element.label}`} />;
}

export function DateFormElementConfig({ element, onChange }: { element: DateFormElementType; onChange?: (updates: Partial<DateFormElementType>) => void }) {
  const form = useFormContext<{ elements: Array<{ id: string; kind: FormElementKind; config?: { isSubmissionDate?: boolean } }> }>();
  const formElements = form.watch('elements');

  const isSubmissionDateTaken = formElements.some((el) => el.kind === FormElementKind.Date && el.id !== element.id && el.config?.isSubmissionDate);

  const isSubmissionDateDisabled = isSubmissionDateTaken && !element.config?.isSubmissionDate;

  return (
    <div className="flex flex-col gap-4">
      <ConfigLabel>
        <span>Label:</span>
        <Input value={element.label} onChange={(e) => onChange?.({ label: e.target.value })} />
      </ConfigLabel>

      <ConfigLabel className="flex-row items-start gap-2">
        <Checkbox checked={element.required} onCheckedChange={(checked) => onChange?.({ required: !!checked })} />
        <span>Required</span>
      </ConfigLabel>

      {element.config?.showTime && (
        <ConfigLabel className="flex-row items-start gap-2">
          {isSubmissionDateDisabled ? (
            <TooltipProvider>
              <Tooltip delayDuration={0}>
                <TooltipTrigger asChild>
                  <span>
                    <Checkbox checked={false} disabled />
                  </span>
                </TooltipTrigger>
                <TooltipPortal>
                  <TooltipContent side="top" className="bg-default-text text-white">
                    Only one date field can be used as the submission date per form
                    <TooltipArrow className="fill-default-text" />
                  </TooltipContent>
                </TooltipPortal>
              </Tooltip>
            </TooltipProvider>
          ) : (
            <Checkbox
              checked={element.config?.isSubmissionDate ?? false}
              onCheckedChange={(checked) => onChange?.({ config: { ...element.config, isSubmissionDate: !!checked } } as Partial<DateFormElementType>)}
            />
          )}
          <span>{DateConfigLabels.isSubmissionDate}</span>
        </ConfigLabel>
      )}
    </div>
  );
}
